<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <div>
        <h1 class="display-4 mb-1"><strong>{{ headerTitle }}</strong></h1>
        <p v-if="isPublicContext" class="text-muted mb-0"><small>{{ $t('recorded_trails.created_by_public_display_name', { public_display_name: publicDisplayName }) }}</small></p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <template v-if="!isPublicContext && !selectionMode">
          <a href="/map?get_user_recordings=true" class="btn btn-sm btn-secondary">{{ $t('recorded_trails.view_on_map') }}</a>
          <button class="btn btn-sm btn-secondary" @click="startImport"><i class="material-icons me-2">upload</i>{{ $t('import_files.title') }}</button>
          <!-- Only surfaces once there is something to restore -->
          <button
            v-if="deletedCount > 0"
            class="btn btn-sm btn-secondary d-flex align-items-center"
            type="button"
            data-testid="deleted-recordings-open"
            @click="openDeletedRecordings"
          >
            <i class="material-icons me-2">restore_from_trash</i>
            {{ $t('recorded_trails.bin_count', { count: deletedCount }) }}
          </button>
        </template>
        <button
          v-if="!isPublicContext"
          class="btn btn-sm d-flex align-items-center"
          :class="selectionMode ? 'btn-outline-secondary' : 'btn-secondary'"
          @click="toggleSelectionMode"
        >
          <i class="material-icons me-2">{{ selectionMode ? 'close' : 'delete' }}</i>
          {{ selectionMode ? $t('shared.cancel_2') : $t('shared.delete') }}
        </button>
        <button
          v-if="!selectionMode"
          class="btn btn-secondary d-flex align-items-center justify-content-center rounded-circle p-0"
          style="width:35px;height:35px !important;"
          @click="shareCurrentPage"
          type="button"
        >
          <span class="material-icons" style="font-size:17px">share</span>
        </button>
      </div>
    </div>

    <!-- Filters Row -->
    <div class="filters-row d-flex gap-2 mb-3">
      <!-- Clear All Filters Button -->
      <button v-if="hasActiveFilters" class="btn btn-sm btn-filter btn-clear-all" @click="clearAllFilters">
        <i class="material-icons me-1">clear</i>
        {{ $t('common.clear_all') }}
      </button>

      <!-- Date Range Filter -->
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="dateDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDateFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ $t('recorded_trails.date') }} <i class="material-icons">expand_more</i>
          <span v-if="isDateFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>{{ $t('recorded_trails.recorded_between') }}</small>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <input class="form-control" type="date" v-model="startDateDraft" />
            <span>–</span>
            <input class="form-control" type="date" v-model="endDateDraft" />
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetDateFilter">{{ $t('shared.clear') }}</button>
            <button class="btn btn-sm btn-primary" @click="applyDateFilter">{{ $t('shared.apply') }}</button>
          </div>
        </div>
      </div>

      <!-- Sort Filter -->
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="sortDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isSortFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ $t('shared.sort_by') }} <i class="material-icons">expand_more</i>
          <span v-if="isSortFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>{{ $t('shared.select_sort') }}</small>
          </div>
          <div class="mb-2" v-for="option in sortOptions" :key="option.value">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="sort" :value="option.value" v-model="sortDraft" />
              <span>{{ option.label }}</span>
            </label>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetSortFilter">{{ $t('shared.reset') }}</button>
            <button class="btn btn-sm btn-primary" @click="applySortFilter">{{ $t('shared.apply') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Row -->
    <div class="search-row d-flex gap-3 mb-4">
      <div class="search-input-wrapper flex-grow-1">
        <i class="material-icons search-icon">search</i>
        <input class="form-control search-input" type="text" v-model="searchString" @keyup.enter="searchRecordedTrailsClicked($event, true)" :placeholder="$t('recorded_trails.search_by_name_or_keyword')" />
      </div>
      <button class="btn btn-primary" @click="searchRecordedTrailsClicked($event, true)">{{ $t('shared.search') }}</button>
    </div>

    <!-- Main list of recorded trails -->
    <div>
      <!-- If we have recorded trails, show them -->
      <div v-if="recordedTrails.length">
        <!-- Loop using new component -->
        <RecordedTrailItem
          v-for="(trail, index) in recordedTrails"
          :key="trail.id || index"
          :trail="trail"
          :is-public="isPublicContext"
          :selection-mode="selectionMode"
          :is-selected="selectedRecordingIds.has(trail.id)"
          @show-flyover="showFlyover"
          @edit-trail="editRecordedTrail"
          @export-gpx="exportGpx"
          @copy-share="triggerCopyModel"
          @crop-trail="cropRecordedTrail"
          @delete-trail="deleteRecordedTrail"
          @selection-changed="toggleRecordingSelection"
        />
      </div>

      <!-- If no hikes found -->
      <div v-else class="text-center py-5">
        <p class="text-muted">{{ $t('recorded_trails.no_recorded_hikes_found') }}</p>
      </div>

      <!-- "See More" Button -->
      <div v-if="hasMoreTrails" class="see-more d-flex justify-content-center m-0">
        <button class="btn btn-link" @click="searchRecordedTrailsClicked($event, false)">
          {{ $t('recorded_trails.see_more') }}
        </button>
      </div>

      <!-- Bulk Delete Floating Action Bar -->
      <transition name="slide-up">
        <div v-if="selectionMode && !isPublicContext" class="bulk-action-bar">
          <div class="bulk-action-content">
            <div class="selection-info">
              <span v-if="selectedRecordingIds.size === 0" class="text-muted">{{ $t('recorded_trails.select_recordings_to_delete') }}</span>
              <span v-else>{{ $t('recorded_trails.selected_recording_ids_recording_selected_recording', { selected_recording_ids: selectedRecordingIds.size, selected_recording_ids_2: selectedRecordingIds.size !== 1 ? 's' : '' }) }}</span>
            </div>
            <div class="bulk-actions d-flex gap-2">
              <button
                v-if="selectedRecordingIds.size > 0 && selectedRecordingIds.size < recordedTrails.length"
                class="btn btn-sm btn-link"
                @click="selectAllRecordings"
              >
                {{ $t('recorded_trails.select_all') }}
              </button>
              <button
                v-if="selectedRecordingIds.size === recordedTrails.length && recordedTrails.length > 0"
                class="btn btn-sm btn-link"
                @click="deselectAllRecordings"
              >
                {{ $t('recorded_trails.deselect_all') }}
              </button>
              <button
                class="btn btn-sm btn-secondary"
                @click="toggleSelectionMode"
              >
                {{ $t('shared.cancel_2') }}
              </button>
              <button
                class="btn btn-sm btn-danger"
                :disabled="selectedRecordingIds.size === 0 || bulkDeleteInProgress"
                @click="bulkDeleteRecordings"
              >
                <span v-if="bulkDeleteInProgress" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ bulkDeleteInProgress ? $t('common.deleting') : (selectedRecordingIds.size ? $t('recorded_trails.delete_count', { count: selectedRecordingIds.size }) : $t('shared.delete')) }}
              </button>
            </div>
          </div>
        </div>
      </transition>

      <LoadingDialog :show-loading-dialog="loading" />

      <CopyLinkModal ref="shareModal" :helper-text="$t('recorded_trails.anyone_with_link_can_view')" />
    </div>

    <!-- Dialog for the "Deleted (n)" header button; renders nothing until opened -->
    <DeletedRecordings
      v-if="!isPublicContext"
      ref="deletedRecordings"
      @restored="onRecordingRestored"
      @count-changed="deletedCount = $event"
    />

    <!-- Edit Recording Details Component -->
    <EditRecordingDetails v-if="!isPublicContext" />
  </div>
</template>

<script lang="ts">
import graph_client from '../../utils/graphql/graph_client';
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import {
  DestroyRecording,
  GetRecordedTrailById,
  SearchUsersRecordedTrails,
  UserRecordingsQuery
} from "../../utils/graphql/recorded-trail-graph";
import { SearchPublicRecordings } from "../../utils/graphql/public-recorded-trail-graph";
import EditRecordingDetails from "../../apps/edit_recording_details/EditRecordingDetails.vue";
import RecordedTrailItem from "./components/RecordedTrailItem.vue";
import DeletedRecordings from "./components/DeletedRecordings.vue";
import CopyLinkModal from "../../utils/shared_components/CopyLinkModal.vue";
import { Dropdown } from 'bootstrap';
import { IMPORT_ENTRY_POINTS, openImportFiles } from '../import_files/openImportFiles';
import moment from 'moment'
import {getFeatureCollectionLineString} from "../../utils/getFeatureCollectionLineString";
import {geojsonToGpx} from "../../utils/geojsonToGpx";
import {
  RECORDING_SORT_DEFAULT,
  RECORDING_SORT_OPTIONS,
  isRecordingSortActive,
  normalizeRecordingSort,
} from "../../utils/helpers/recordingSortOptions";

export default {
  name: 'RecordedTrails',
  components: { LoadingDialog, EditRecordingDetails, RecordedTrailItem, CopyLinkModal, DeletedRecordings },
  props: {
    formMode: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    isPublicContext() {
      return this.appConfig.context === 'public'
    },
    headerTitle() {
      return this.isPublicContext ? this.$t('shared.recordings') : this.$t('recorded_trails.my_recordings')
    },
    publicDisplayName() {
      return this.appConfig.displayName || ''
    },
    isDateFilterActive() {
      return this.startDate !== null || this.endDate !== null
    },
    sortOptions() {
      return RECORDING_SORT_OPTIONS
    },
    isSortFilterActive() {
      return isRecordingSortActive(this.sortApplied)
    },
    hasActiveFilters() {
      return this.isDateFilterActive || this.isSortFilterActive || this.searchString.trim() !== ''
    }
  },
  data() {
    const globalConfig = window.recordedTrailsAppConfig || {}
    const defaultUserId = globalConfig.currentUserId || (window.user && window.user.id ? String(window.user.id) : null)
    return {
      appConfig: {
        context: globalConfig.context || 'private',
        firebaseUserId: globalConfig.firebaseUserId || null,
        displayName: globalConfig.displayName || ''
      },
      recordedTrails: [],
      // Owned here rather than read off the child ref so the header button is reactive;
      // the DeletedRecordings dialog reports it via @count-changed.
      deletedCount: 0,
      searchString: '',
      // Date filter states
      startDate: null,
      endDate: null,
      startDateDraft: null,
      endDateDraft: null,
      // Sort filter states. Applied server-side via the `sort` query argument.
      sortApplied: RECORDING_SORT_DEFAULT,
      sortDraft: RECORDING_SORT_DEFAULT,
      currentPage: 1,
      pageSize: 10,
      hasMoreTrails: false,
      loading: true,
      currentUserId: globalConfig.context === 'public' ? null : defaultUserId,
      // Bulk delete / selection mode state
      selectionMode: false,
      selectedRecordingIds: new Set(),
      bulkDeleteInProgress: false,
    };
  },
  mounted() {
    this.searchRecordedTrails(true);
    window.reloadRecordedTrails = () => {
      this.searchRecordedTrails(true);
    };

    // Initialize dropdowns with fixed positioning to escape overflow container
    this.$nextTick(() => {
      this.initializeDropdowns();
    });

    // Attach dropdown event listeners to sync draft/applied state
    if (this.$refs.dateDropdownToggle) {
      this.$refs.dateDropdownToggle.addEventListener('show.bs.dropdown', this.onDateDropdownShow);
      this.$refs.dateDropdownToggle.addEventListener('hide.bs.dropdown', this.onDateDropdownHide);
    }
    if (this.$refs.sortDropdownToggle) {
      this.$refs.sortDropdownToggle.addEventListener('show.bs.dropdown', this.onSortDropdownShow);
      this.$refs.sortDropdownToggle.addEventListener('hide.bs.dropdown', this.onSortDropdownHide);
    }
  },
  beforeUnmount() {
    // Clean up dropdown event listeners
    if (this.$refs.dateDropdownToggle) {
      this.$refs.dateDropdownToggle.removeEventListener('show.bs.dropdown', this.onDateDropdownShow);
      this.$refs.dateDropdownToggle.removeEventListener('hide.bs.dropdown', this.onDateDropdownHide);
    }
    if (this.$refs.sortDropdownToggle) {
      this.$refs.sortDropdownToggle.removeEventListener('show.bs.dropdown', this.onSortDropdownShow);
      this.$refs.sortDropdownToggle.removeEventListener('hide.bs.dropdown', this.onSortDropdownHide);
    }
  },
  methods: {
    // Import files, starting from Recordings; the list reloads through window.reloadRecordedTrails.
    startImport() {
      openImportFiles(IMPORT_ENTRY_POINTS.recordings);
    },
    selectRecordedTrail(event, trail) {
      event.preventDefault();
      window.trailSelected(trail.id);
      window.trailSelectedName(trail.title);
      window.closeVueModal();
    },
    formatDate(date) {
      return moment(date).format('LLL');
    },
    recordingOverviewUrl(trail) {
      if (trail && trail.obfuscatedSlug) {
        return `/recordings/${trail.obfuscatedSlug}`;
      }
      if (trail && trail.shareableUrl) {
        return trail.shareableUrl;
      }
      if (trail && trail.id) {
        return `/user/recordings/${trail.id}`;
      }
      return '#';
    },
    recordingMapUrl(trail) {
      const baseUrl = this.recordingPublicBaseUrl(trail);
      if (baseUrl === '#') {
        return baseUrl;
      }

      return `${baseUrl}/map`;
    },
    recordingCropUrl(trail) {
      const baseUrl = this.recordingPublicBaseUrl(trail);
      if (baseUrl === '#') {
        return baseUrl;
      }

      return `${baseUrl}/crop`;
    },
    recordingPublicBaseUrl(trail) {
      if (trail && trail.obfuscatedSlug) {
        return `/recordings/users/${trail.obfuscatedSlug}`;
      }
      if (trail && trail.shareableUrl) {
        return trail.shareableUrl;
      }
      return '#';
    },
    canDisplayTrail(trail) {
      if (this.isPublicContext) return true
      if (!trail) {
        return false;
      }

      if (trail.public) {
        return true;
      }

      const ownerId = trail.user && trail.user.id ? String(trail.user.id) : null;
      if (!ownerId) {
        return false;
      }

      if (this.currentUserId && String(this.currentUserId) === ownerId) {
        return true;
      }

      return false;
    },
    realTrailUrl(t) {
      // t is the 'trail.trail' object
      // e.g. { countrySlug: 'ireland', regionSlug: 'county-kerry', slug: 'kerry-way' }
      return `/trails/${t.countrySlug}/${t.regionSlug}/${t.slug}`;
    },
    searchRecordedTrailsClicked(event, reset = false) {
      event.preventDefault();
      this.searchRecordedTrails(reset);
    },
    searchRecordedTrails(reset = false) {
      this.loading = true;
      if (reset) {
        this.currentPage = 1;
        this.recordedTrails = [];
      }

      const variables = {
        string: this.searchString || '',
        startDate: this.startDate || null,
        endDate: this.endDate || null,
        page: this.currentPage,
        pageSize: this.pageSize,
        sort: normalizeRecordingSort(this.sortApplied),
      };

      if (this.isPublicContext) {
        if (!this.appConfig.firebaseUserId) {
          this.recordedTrails = []
          this.hasMoreTrails = false
          this.loading = false
          return
        }
        variables.firebaseUserId = this.appConfig.firebaseUserId
      }

      const requestPromise = this.isPublicContext
        ? graph_client.request(SearchPublicRecordings, variables)
        : graph_client.request(SearchUsersRecordedTrails, variables)

      requestPromise.then((data) => {
        const rawTrails = this.isPublicContext
          ? data.searchPublicRecordings || []
          : data.recordingsTextSearch || []
        // Server-ordered by the `sort` variable — do NOT re-sort here. Re-sorting
        // would only order the pages fetched so far, so e.g. "longest first" would
        // hide the actual longest recording until every page had been loaded.
        const newTrails = this.isPublicContext
          ? rawTrails
          : rawTrails.filter((trail) => this.canDisplayTrail(trail))

        this.recordedTrails = reset ? newTrails : this.recordedTrails.concat(newTrails)
        this.hasMoreTrails = rawTrails.length === this.pageSize
        if (this.hasMoreTrails) {
          this.currentPage += 1
        }
      }).catch((error) => {
        console.error('Error fetching recorded trails:', error);
      }).finally(() => {
        this.loading = false
      });
    },
    clearSearchFilters(event) {
      event.preventDefault();
      this.searchString = '';
      this.startDate = null;
      this.endDate = null;
      this.searchRecordedTrails(true);
    },
    clearAllFilters() {
      this.searchString = '';
      this.startDate = null;
      this.endDate = null;
      this.startDateDraft = null;
      this.endDateDraft = null;
      this.sortApplied = RECORDING_SORT_DEFAULT;
      this.sortDraft = RECORDING_SORT_DEFAULT;
      this.searchRecordedTrails(true);
    },
    resetDateFilter() {
      this.startDateDraft = null;
      this.endDateDraft = null;
      this.applyDateFilter();
    },
    applyDateFilter() {
      this.startDate = this.startDateDraft;
      this.endDate = this.endDateDraft;
      this.searchRecordedTrails(true);
      this.$nextTick(() => {
        try {
          if (this.$refs.dateDropdownToggle) {
            const dropdown = Dropdown.getOrCreateInstance(this.$refs.dateDropdownToggle);
            dropdown.hide();
          }
        } catch (e) { /* noop */ }
      });
    },
    resetSortFilter() {
      this.sortDraft = RECORDING_SORT_DEFAULT;
      this.applySortFilter();
    },
    applySortFilter() {
      this.sortApplied = this.sortDraft;
      this.searchRecordedTrails(true);
      this.$nextTick(() => {
        try {
          if (this.$refs.sortDropdownToggle) {
            const dropdown = Dropdown.getOrCreateInstance(this.$refs.sortDropdownToggle);
            dropdown.hide();
          }
        } catch (e) { /* noop */ }
      });
    },
    onDateDropdownShow() {
      this.startDateDraft = this.startDate;
      this.endDateDraft = this.endDate;
    },
    onDateDropdownHide() {
      this.startDateDraft = this.startDate;
      this.endDateDraft = this.endDate;
    },
    onSortDropdownShow() {
      this.sortDraft = this.sortApplied;
    },
    onSortDropdownHide() {
      this.sortDraft = this.sortApplied;
    },
    initializeDropdowns() {
      const dropdownElements = [this.$refs.dateDropdownToggle, this.$refs.sortDropdownToggle].filter(Boolean);

      dropdownElements.forEach(el => {
        if (el) {
          Dropdown.getOrCreateInstance(el, {
            popperConfig: {
              // Use fixed positioning so menus aren't clipped by scroll containers
              strategy: 'fixed',
              modifiers: [
                { name: 'computeStyles', options: { adaptive: false } },
                { name: 'preventOverflow', options: { boundary: 'viewport', altBoundary: true, tether: false } },
              ]
            }
          });
        }
      });
    },
    editRecordedTrail(trail) {
      if (this.isPublicContext) return;
      window.showEditRecordingModal(trail.obfuscatedSlug);
    },
    cropRecordedTrail(trail) {
      if (this.isPublicContext) return;
      const croppingUrl = this.recordingCropUrl(trail);
      if (croppingUrl !== '#') {
        window.location.href = croppingUrl;
      }
    },
    async exportGpx(trail) {
      const response = await graph_client.request(GetRecordedTrailById, { slug: `${trail.obfuscatedSlug}` });
      const { recordingBySlug } = response;
      const recording = recordingBySlug;
      const geojson = JSON.parse(recording.lineString);
      const coords = getFeatureCollectionLineString(geojson);

      const gpx = geojsonToGpx({
        type: "Feature",
        properties: {}, // Add relevant properties if needed
        geometry: {
          type: "LineString",
          coordinates: coords,
        }
      }, recording.timeStamps,{
        creator: 'Hiiker', // Set creator metadata
        metadata: {
          name: recording.name, // Add trail name to GPX metadata
          time: recording.createdAt // Add creation time if available
        }
      });

      const blob = new Blob([gpx], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${recording.name || 'track'}.gpx`; // Use trail name for filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    getFeatureCollectionLineString(geojson) {
      return getFeatureCollectionLineString(geojson);
    },
    showFlyover(_event, trail, shareConfig = null) {
      window.showMapModalForRecordedTrail(trail.id, shareConfig);
    },
    printRecordedTrail(trail) {
      return `/print-maps?data_ids[]=${trail.id}&data_types[]=Recording`;
    },
    triggerCopyModel(_event, trail) {
      const baseUrl = trail && trail.shareableUrl ? trail.shareableUrl : null;
      const fallbackUrl = this.recordingPublicBaseUrl(trail);
      const sharePageLink = this.sharePageLink();
      const trailUrl = baseUrl || (fallbackUrl && fallbackUrl !== '#' ? `${window.location.origin}${fallbackUrl}` : sharePageLink);
      if (trailUrl && this.$refs.shareModal && typeof this.$refs.shareModal.show === 'function') {
        this.$refs.shareModal.show(trailUrl, trail, 'recording');
      }
    },
    shareCurrentPage() {
      const link = this.sharePageLink();
      if (this.$refs.shareModal && typeof this.$refs.shareModal.show === 'function') {
        this.$refs.shareModal.show(link);
      }
    },
    sharePageLink() {
      const firebaseId = this.appConfig.firebaseUserId || window.user?.firebaseUserId || window.user?.firebase_user_id;
      return firebaseId ? `${window.location.origin}/users/${firebaseId}/recordings` : window.location.href;
    },
    deleteRecordedTrail(trail) {
      if (this.isPublicContext) return;
      if (confirm(this.$t('recorded_trails.confirm_delete_one'))) {
        this.loading = true;
        graph_client.request(DestroyRecording, {
          input: { id: trail.id },
        }).then(() => {
          this.searchRecordedTrails(true);
          this.refreshDeletedRecordings();
        }).catch((error) => {
          console.error('Error deleting recorded trail:', error);
        });
      }
    },
    // The delete is soft, so the recording has moved into the Recordings bin rather than
    // gone — reload so the header count picks it up without needing a page refresh.
    refreshDeletedRecordings() {
      (this.$refs.deletedRecordings as any)?.load?.();
    },
    openDeletedRecordings() {
      (this.$refs.deletedRecordings as any)?.open?.();
    },
    onRecordingRestored() {
      this.searchRecordedTrails(true);
    },
    /* ---------- Bulk Delete / Selection Mode Methods ---------- */
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        // Clear selection when exiting selection mode
        this.selectedRecordingIds = new Set();
      }
    },
    toggleRecordingSelection(recordingId) {
      if (this.selectedRecordingIds.has(recordingId)) {
        this.selectedRecordingIds.delete(recordingId);
      } else {
        this.selectedRecordingIds.add(recordingId);
      }
      // Trigger reactivity for Set
      this.selectedRecordingIds = new Set(this.selectedRecordingIds);
    },
    selectAllRecordings() {
      this.recordedTrails.forEach(trail => {
        if (trail.id) this.selectedRecordingIds.add(trail.id);
      });
      this.selectedRecordingIds = new Set(this.selectedRecordingIds);
    },
    deselectAllRecordings() {
      this.selectedRecordingIds = new Set();
    },
    async bulkDeleteRecordings() {
      if (this.isPublicContext) return;
      if (this.selectedRecordingIds.size === 0) return;

      const count = this.selectedRecordingIds.size;
      const message = count === 1
        ? this.$t('recorded_trails.confirm_bulk_delete_one')
        : this.$t('recorded_trails.confirm_bulk_delete_many', { count });

      if (!confirm(message)) {
        return;
      }

      this.bulkDeleteInProgress = true;
      const idsToDelete = Array.from(this.selectedRecordingIds);
      const deletedIds = [];
      const failedIds = [];

      try {
        // Delete in batches of 5 to avoid overwhelming the server
        // (recordings may have more data than hikes)
        const batchSize = 5;
        for (let i = 0; i < idsToDelete.length; i += batchSize) {
          const batch = idsToDelete.slice(i, i + batchSize);

          // Process batch sequentially
          for (const id of batch) {
            try {
              console.log('Attempting to delete recording ID:', id);
              const result = await graph_client.request(DestroyRecording, {
                input: { id }
              });

              console.log('Delete result for ID', id, ':', result);

              // Check if deletion was successful - if we get a result back with an id, it succeeded
              if (result?.destroyRecording && result.destroyRecording.id) {
                console.log('Successfully deleted recording ID:', id);
                deletedIds.push(id);
              } else {
                console.warn('Delete returned no result for ID:', id, 'Full result:', result);
                failedIds.push(id);
              }
            } catch (err) {
              console.error('Delete recording failed for ID:', id, 'Error:', err);
              console.error('Error details:', {
                message: err.message,
                response: err.response,
                graphQLErrors: err.response?.errors
              });
              failedIds.push(id);
            }
          }
        }

        // Remove successfully deleted recordings from the UI
        if (deletedIds.length > 0) {
          this.recordedTrails = this.recordedTrails.filter(t => !deletedIds.includes(t.id));
          this.refreshDeletedRecordings();
        }

        // Show results
        if (failedIds.length === 0) {
          alert(deletedIds.length === 1
            ? this.$t('recorded_trails.bulk_deleted_one')
            : this.$t('recorded_trails.bulk_deleted_many', { count: deletedIds.length }));
        } else if (deletedIds.length === 0) {
          alert(count === 1
            ? this.$t('recorded_trails.bulk_failed_one')
            : this.$t('recorded_trails.bulk_failed_many', { count }));
        } else {
          alert(deletedIds.length === 1
            ? this.$t('recorded_trails.bulk_partial_one', { failed: failedIds.length })
            : this.$t('recorded_trails.bulk_partial_many', { count: deletedIds.length, failed: failedIds.length }));
        }

        // Exit selection mode and refresh if needed
        this.selectionMode = false;
        this.selectedRecordingIds = new Set();

        if (this.hasMoreTrails && deletedIds.length > 0) {
          await this.searchRecordedTrails(false);
        }
      } catch (err) {
        console.error('Bulk delete error:', err);
        alert(this.$t('recorded_trails.bulk_delete_error', { error: err.message || err.toString() }));
      } finally {
        this.bulkDeleteInProgress = false;
      }
    }
  },
};
</script>

<style scoped>
/* Filters Row - Horizontally scrollable to avoid layout push */
.filters-row {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: visible; /* allow dropdown menus to escape vertically */
  -webkit-overflow-scrolling: touch;
  position: relative;
  z-index: 2; /* keep menus above nearby elements */
}

.filters-row > .dropdown,
.filters-row > .btn,
.filters-row > button {
  flex: 0 0 auto;
}

/* Filter Buttons */
.btn-filter {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  position: relative;
  white-space: nowrap;
}

.btn-filter:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

/* Active filter state */
.btn-filter.active {
  background: #2b2c34;
  border-color: #FBCD01;
  color: #fff;
}

/* Filter indicator dot */
.filter-indicator {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  background: #FBCD01;
  border-radius: 50%;
}

/* Clear All Filters Button */
.btn-clear-all {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 500;
  min-width: auto;
}

.btn-clear-all:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

/* Search Input */
.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  pointer-events: none;
}

.search-input {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border-radius: 118px;
  font-size: 14px;
  height: 44px;
}

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
  background: #252530;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* Dropdowns */
.dropdown-toggle::after {
  display: none;
}

.dropdown-menu {
  background: var(--gray);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  z-index: 1100;
}

.dropdown-item {
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 14px;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* See More */
.see-more {
  margin-top: 2rem;
  padding-top: 1rem;
}

.see-more button {
  color: #FBCD01;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
}

.see-more button:hover {
  text-decoration: underline;
}

/* Responsive Tweaks */
@media (max-width: 576px) {
  .filters-row {
    gap: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .filters-row .btn-clear-all {
    flex: 0 0 auto;
    min-width: auto;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }

  .search-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .search-row .btn {
    width: auto;
    align-self: flex-start;
  }

  /* Adjust filter indicators for mobile */
  .filter-indicator {
    top: 4px;
    right: 4px;
    width: 5px;
    height: 5px;
  }
}

/* ---------------- Bulk Delete Action Bar ---------------- */
.bulk-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1f2026;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  padding: 1rem 1.5rem;
}

.bulk-action-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.selection-info {
  font-size: 1rem;
  font-weight: 500;
  color: #fff;
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Slide up transition for action bar */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

@media (max-width: 576px) {
  .bulk-action-bar {
    padding: 0.75rem 1rem;
  }

  .bulk-action-content {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .selection-info {
    text-align: center;
    font-size: 0.875rem;
  }

  .bulk-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
}
</style>
